import { useEffect, useState } from 'react'

import { IconChevronLeft } from '@tabler/icons-react'
import { get, set } from 'local-storage'
import { startCase } from 'lodash'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled from 'styled-components'

import { Breadcrumb, useBreadcrumbs } from '@/context/breadcrumbContext'
import { useLocale } from '@/context/localeContext'
import { useNavigation } from '@/context/navigationContext'
import { NavGroup } from '@/utils/navigationConfig'
import { truncatePathWithTooltip } from '@/utils/truncate'

import { buildPassTheme } from 'src/styles/theme'

const LS_KEY = 'buildpass-breadcrumb-config'

type BreadcrumbConfig = {
  hasBack: boolean
  hasIcon: boolean
}

const Wrapper = styled.nav`
  ${({ theme }) => theme.font.label02}
  font-weight: 600;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  direction: ltr;
  margin-right: auto;
  white-space: nowrap;

  & svg {
    color: ${({ theme }) => theme.colour.secondary._500};
  }
`

type BackProps = {
  show: boolean
}

const BackLink = styled.span<BackProps>`
  width: ${({ show }) =>
    show ? '24px' : '0px'}; // needs to always be rendered to nicely animate
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colour.secondary._500};
  margin-right: 0.5rem;
  transition: 0.2s;

  & svg {
    color: ${({ theme }) => theme.colour.secondary._500};
  }

  &:hover {
    transform: translateX(-0.125rem);

    & svg {
      color: ${({ theme }) => theme.colour.secondary.base};
    }
  }
`

type IconProps = {
  show: boolean
}

const IconWrapper = styled.div<IconProps>`
  width: ${({ show }) => (show ? '18px' : '0px')};
  margin-right: 0.5rem;
  transition: 0.2s;
  display: flex;
`

const PageLink = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colour.secondary._700};
  margin-right: 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover,
  &:focus,
  &:active {
    text-decoration: underline;
    color: ${({ theme }) => theme.colour.secondary.base};
  }

  &::after {
    content: '|';
    margin-left: 0.5rem;
  }
`

const NoLink = styled.span`
  display: flex;
  align-items: center;
  ${({ theme }) => theme.font.label02}
  font-weight: 600;
  color: ${({ theme }) => theme.colour.secondary._500};
  margin-right: 0.5rem;

  &:not(:last-child)::after {
    content: '|';
    margin-left: 0.5rem;
  }
`

type Props = {
  backUrl?: string
}

const findParent = (navItemId: string, navConfig: NavGroup[]) => {
  for (const group of navConfig) {
    const parentNavItem = group.items?.find((item) =>
      item.subItems?.some((i) => i.id === navItemId)
    )

    if (parentNavItem) {
      return parentNavItem
    }
  }
}

const getFallbackBreadcumb = (pathname: string): Breadcrumb[] => {
  const route = pathname.split('/').find((r) => r !== '')
  return [{ text: startCase(route) }]
}

export const CorePageHeaderBreadcrumbs: React.FC<Props> = ({ backUrl }) => {
  const breadcrumbs = useBreadcrumbs()
  const { pathname } = useRouter()
  const { currentNavItem, navigationConfig } = useNavigation()

  const [config, setConfig] = useState<BreadcrumbConfig>(
    get<BreadcrumbConfig>(LS_KEY) || { hasBack: false, hasIcon: false }
  )

  const parent = findParent(currentNavItem?.id, navigationConfig)

  const { t } = useLocale()

  const Icon = breadcrumbs?.find((b) => b.icon)?.icon || parent?.icon

  const crumbs = breadcrumbs?.length
    ? breadcrumbs
    : getFallbackBreadcumb(pathname)

  useEffect(() => {
    // store config to gracefully animate breadcrumb changes
    if (config.hasBack !== !!backUrl || config.hasIcon !== !!Icon) {
      const newConfig = { hasBack: !!backUrl, hasIcon: !!Icon }
      setConfig(newConfig)
      set(LS_KEY, newConfig)
    }
  }, [backUrl, Icon, config])

  return (
    <Wrapper>
      <Link
        href={backUrl ?? ''}
        style={{
          position: 'sticky',
          left: 0,
          paddingLeft: '1rem',
          background: buildPassTheme.colour.neutral.background,
        }}
      >
        <BackLink show={config.hasBack}>
          <IconChevronLeft size={24} />
        </BackLink>
      </Link>

      <IconWrapper
        show={config.hasIcon}
        style={{
          transform: currentNavItem?.iconRotate
            ? `rotate(${currentNavItem?.iconRotate}deg)`
            : undefined,
        }}
      >
        {Icon && <Icon size={18} />}
      </IconWrapper>

      {/* the below is essentially exclusively for registers */}
      {parent?.path && (
        <Link href={parent.path} key={t(parent?.title)}>
          <PageLink>{t(parent?.title)}</PageLink>
        </Link>
      )}

      {crumbs.map((b) => {
        const text = t(b.text)

        if (b.path) {
          return (
            <Link href={b.path} key={text}>
              <PageLink>{truncatePathWithTooltip(text, 60)}</PageLink>
            </Link>
          )
        }
        return <NoLink key={text}>{text}</NoLink>
      })}
    </Wrapper>
  )
}
